import React, { useState, useEffect } from 'react';
import { Search, Filter, Download, User, Phone, Calendar, CheckCircle, XCircle, Clock, MessageSquare } from 'lucide-react';
import api from '../../services/api';

function LeadsSDRVoz() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    produto: ''
  });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [itemsPerPage] = useState(20);

  useEffect(() => {
    carregarLeads();
  }, [page, filters]);

  const carregarLeads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: itemsPerPage,
        offset: (page - 1) * itemsPerPage,
        ...(searchTerm && { busca: searchTerm }),
        ...filters
      });

      const response = await api.get(`/sdr-voz/leads-contatados?${params}`);
      setLeads(response.data.data || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Erro ao carregar leads:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'contatado':
        return 'bg-blue-100 text-blue-800';
      case 'interessado':
        return 'bg-green-100 text-green-800';
      case 'nao_interessado':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'contatado':
        return <Phone size={16} />;
      case 'interessado':
        return <CheckCircle size={16} />;
      case 'nao_interessado':
        return <XCircle size={16} />;
      default:
        return <Clock size={16} />;
    }
  };

  const exportarExcel = () => {
    window.location.href = `/api/sdr-voz/leads-contatados/export?${new URLSearchParams({
      ...filters,
      ...(searchTerm && { busca: searchTerm })
    })}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Leads SDR de Voz</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Leads contatados pelo SDR por voz com status de interesse
          </p>
        </div>
        <button onClick={exportarExcel} className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
          <Download size={16} /> Exportar Excel
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nome ou número..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          <select
            value={filters.status}
            onChange={(e) => setFilters({...filters, status: e.target.value})}
            className="px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            <option value="">Todos os status</option>
            <option value="contatado">Contatado</option>
            <option value="interessado">Interessado</option>
            <option value="nao_interessado">Não interessado</option>
          </select>

          <select
            value={filters.produto}
            onChange={(e) => setFilters({...filters, produto: e.target.value})}
            className="px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            <option value="">Todos os produtos</option>
            <option value="Ingarden">Ingarden</option>
            <option value="Lumiere Residence Club">Lumiere</option>
            <option value="Benjamin Constant Duplex">Benjamin</option>
          </select>
        </div>
      </div>

      {/* Tabela de Leads */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-gray-700 text-sm">
              Leads ({leads.length}/{total})
            </h2>
            <span className="text-xs text-gray-500">
              Página {page} de {Math.ceil(total / itemsPerPage)}
            </span>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : leads.length === 0 ? (
          <div className="py-12 text-center text-gray-300">
            <User size={48} className="mx-auto mb-2 opacity-40" />
            <p className="text-sm">Nenhum lead encontrado</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {leads.map(lead => (
              <div key={lead.id} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      lead.status === 'interessado' ? 'bg-green-100' :
                      lead.status === 'nao_interessado' ? 'bg-red-100' : 'bg-blue-100'
                    }`}>
                      {getStatusIcon(lead.status)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">{lead.nome}</p>
                      <p className="text-xs text-gray-500 font-mono">{lead.numero}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(lead.status)}`}>
                        {lead.status === 'contatado' && 'Contatado'}
                        {lead.status === 'interessado' && 'Interessado'}
                        {lead.status === 'nao_interessado' && 'Não interessado'}
                      </span>
                      <span className="text-xs text-gray-500">{lead.produto}</span>
                    </div>
                    <p className="text-xs text-gray-400">
                      {new Date(lead.data_contato).toLocaleString('pt-BR')}
                    </p>
                  </div>
                </div>
                {lead.observacoes && (
                  <p className="text-sm text-gray-600 mt-2 ml-13">{lead.observacoes}</p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Paginação */}
        {total > itemsPerPage && (
          <div className="px-4 py-3 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-3 py-1 border border-gray-200 rounded text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Anterior
              </button>
              <span className="text-sm text-gray-500">
                Página {page} de {Math.ceil(total / itemsPerPage)}
              </span>
              <button
                onClick={() => setPage(Math.min(Math.ceil(total / itemsPerPage), page + 1))}
                disabled={page === Math.ceil(total / itemsPerPage)}
                className="px-3 py-1 border border-gray-200 rounded text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default LeadsSDRVoz;